<script setup lang="ts">
import { computed, nextTick, shallowRef, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { CartoonWorkItem } from '@/api/cartoon/type'
import AppErrorState from '@/components/app-error-state'
import DreamaLoginLogo from '@/components/dreama-login-logo'
import LoginPrompt from '@/components/login-prompt'
import CartoonProfileHeader from '@/components/cartoon-profile-header'
import ProfileWorkFilterDropdown from '@/components/profile-work-filter-dropdown'
import SummaryContentCard from '@/components/summary-content-card'
import {
  CARTOON_WORK_FILTERS,
  useCartoonProfile,
  type CartoonWorkFilter,
} from '@/composables/useCartoonProfile'
import { useElementVisibility } from '@/composables/useElementVisibility'
import { useRouteRefreshSignal } from '@/composables/useRouteRefreshSignal'
import { useScreenSize } from '@/composables/useScreenSize'
import { useToast } from '@/composables/useToast'
import { useThemeStore } from '@/stores/theme'
import { useUserStore, type UserInfo } from '@/stores/user'
import { openLegacyPlayerUrl } from '@/utils/legacy-player'
import { loginWithBlankClose } from '@/utils/login'
import { buildSummaryContentViewUrl, resolveSummaryContentKind } from '@/utils/profile/content-card'
import { resolveSummaryContentColumnCount } from '@/utils/profile/content-grid'
import { applyDocumentTitle } from '@/utils/page-title'
import { getRuntimePerformanceBudget } from '@/utils/performance/runtime-budget'
import { stateImages } from '@/utils/state-images'
import loadingImage from '@/assets/images/profile-loading.webp'

defineOptions({
  name: 'CartoonView',
})

const route = useRoute()
const router = useRouter()
const toast = useToast()
const themeStore = useThemeStore()
const userStore = useUserStore()
const runtimeBudget = getRuntimePerformanceBudget()
const { screenWidth } = useScreenSize()
const loadMoreTarget = shallowRef<HTMLElement | null>(null)
const unavailableCtaRef = useTemplateRef<HTMLButtonElement>('unavailableCtaRef')
const cartoonId = computed(() => {
  const queryRole = typeof route.query.role === 'string' ? route.query.role : ''
  const routeId = typeof route.params.cartoonId === 'string' ? route.params.cartoonId : ''

  return routeId || queryRole
})
const {
  profile,
  currentItems,
  activeTab,
  activeWorkFilter,
  loadingWorks,
  loadingAppearances,
  loadingMore,
  followLoading,
  error,
  unavailableReason,
  listLoginRequired,
  hasNext,
  isInitialLoading,
  canLoadMore,
  setActiveTab,
  setActiveWorkFilter,
  refresh,
  loadMore,
  toggleFollow,
} = useCartoonProfile(cartoonId)
const { isVisible: isLoadMoreVisible } = useElementVisibility(loadMoreTarget, {
  rootMargin: runtimeBudget.isConstrained ? '240px 0px' : '420px 0px',
  threshold: 0,
})
const isMine = computed(() =>
  Boolean(profile.value?.user?.id && profile.value.user.id === userStore.getId()),
)
const isFollowing = computed(() => profile.value?.hasFollow === 1)
const showInitialSkeleton = computed(
  () => isInitialLoading.value && currentItems.value.length === 0,
)
const showContentLoginPrompt = computed(
  () => Boolean(profile.value) && (!userStore.isLoggedIn() || listLoginRequired.value),
)
const contentLoginMessage = computed(() =>
  profile.value?.name ? `登录后查看 ${profile.value.name} 的更多作品` : '登录后查看更多内容',
)
const showEmpty = computed(
  () =>
    !error.value &&
    !showContentLoginPrompt.value &&
    !showInitialSkeleton.value &&
    !loadingWorks.value &&
    !loadingAppearances.value &&
    currentItems.value.length === 0,
)
const showFullError = computed(() => Boolean(error.value && !profile.value))
const cartoonUnavailableState = computed(() => {
  if (unavailableReason.value === 'not-found') {
    return {
      title: '这个角色不存在',
      subtitle: '链接可能已失效，回到发现页看看其他角色。',
      alt: '角色不存在',
    }
  }

  if (unavailableReason.value === 'unpublished') {
    return {
      title: '这个角色还未发布',
      subtitle: '角色还在准备中，回到发现页看看更多。',
      alt: '角色未发布',
    }
  }

  return null
})
const activeWorkFilterLabel = computed(
  () =>
    CARTOON_WORK_FILTERS.find((filter) => filter.key === activeWorkFilter.value)?.label ?? '作品',
)
const stateMessage = computed(() => {
  if (error.value) {
    return error.value
  }

  if (activeTab.value === 'appearances') {
    return '这个角色还没有出镜内容'
  }

  return activeWorkFilter.value === 'all'
    ? '这个角色还没有作品'
    : `这个角色还没有${activeWorkFilterLabel.value}作品`
})
const columnCount = computed(() => resolveSummaryContentColumnCount(screenWidth.value))
const gridStyle = computed(() => ({ '--cartoon-grid-columns': String(columnCount.value) }))
const workColumns = computed(() => distributeWorks(currentItems.value, columnCount.value))
const skeletonColumns = computed(() =>
  distributeByIndex(
    Array.from({ length: Math.max(10, columnCount.value * 2) }, (_, index) => index),
    columnCount.value,
  ),
)

function openWork(item: CartoonWorkItem) {
  const url = buildSummaryContentViewUrl(item, themeStore.currentTheme)

  if (url) {
    openLegacyPlayerUrl(url)
  }
}

function openAuthor(item?: { user?: { uid?: string | null } | null }) {
  const uid = item?.user?.uid

  if (!uid) {
    return
  }

  void router.push(`/${uid}`)
}

function distributeByIndex<T>(items: T[], count: number): T[][] {
  const columns = Array.from({ length: Math.max(1, count) }, () => [] as T[])

  items.forEach((item, index) => {
    columns[index % columns.length]?.push(item)
  })

  return columns
}

function distributeWorks(items: CartoonWorkItem[], count: number): CartoonWorkItem[][] {
  const columns = Array.from({ length: Math.max(1, count) }, () => [] as CartoonWorkItem[])
  const heights = Array.from({ length: columns.length }, () => 0)

  items.forEach((item) => {
    const targetIndex = heights.indexOf(Math.min(...heights))
    const nextHeight = (heights[targetIndex] ?? 0) + estimateWorkHeight(item)

    columns[targetIndex]?.push(item)
    heights[targetIndex] = nextHeight
  })

  return columns
}

function estimateWorkHeight(item: CartoonWorkItem) {
  const kind = resolveSummaryContentKind(item)

  if (kind === 'radio') {
    return 1.38
  }

  if (kind === 'media-imgtxt') {
    return 1.72
  }

  return 2.0
}

async function handleFollowClick() {
  try {
    if (!userStore.isLoggedIn()) {
      const loggedIn = await promptLogin()

      if (!loggedIn) {
        return
      }
    }

    const nextAction = isFollowing.value ? 'unfollow' : 'follow'

    await toggleFollow()
    toast.success(nextAction === 'follow' ? '已关注' : '已取消关注')
  } catch (err) {
    toast.info(err instanceof Error ? err.message : '关注状态更新失败')
  }
}

function handleWorkFilterChange(filter: string) {
  setActiveWorkFilter(filter as CartoonWorkFilter)
}

function backToFeed() {
  void router.replace('/feed')
}

async function promptLogin() {
  const userInfo = await loginWithBlankClose({
    logo: DreamaLoginLogo,
  })

  if (!userInfo) {
    return false
  }

  userStore.setUserInfo(userInfo as unknown as UserInfo)
  return true
}

watch([isLoadMoreVisible, canLoadMore], ([visible, canMore]) => {
  if (visible && canMore) {
    void loadMore()
  }
})

watch(
  cartoonUnavailableState,
  async (state) => {
    if (!state) {
      return
    }

    applyDocumentTitle(state.title)
    await nextTick()
    unavailableCtaRef.value?.focus()
  },
  { flush: 'post' },
)

watch(
  () => profile.value?.name ?? '',
  (name) => {
    if (name) {
      applyDocumentTitle(name)
    }
  },
  { immediate: true },
)

useRouteRefreshSignal('character', async () => {
  await refresh()
})
</script>

<template>
  <section class="cartoon-page">
    <div class="cartoon-page__inner">
      <div
        v-if="cartoonUnavailableState"
        class="cartoon-page__state cartoon-page__state--stage"
        aria-labelledby="cartoon-unavailable-title"
      >
        <img
          class="cartoon-page__state-image"
          :src="stateImages.empty"
          :alt="cartoonUnavailableState.alt"
          width="200"
          height="200"
          draggable="false"
        />
        <div class="cartoon-page__state-copy">
          <h1 id="cartoon-unavailable-title" class="cartoon-page__state-title">
            {{ cartoonUnavailableState.title }}
          </h1>
          <p class="cartoon-page__state-subtitle">{{ cartoonUnavailableState.subtitle }}</p>
        </div>
        <button
          ref="unavailableCtaRef"
          type="button"
          class="cartoon-page__state-action"
          @click="backToFeed"
        >
          回到发现页
        </button>
      </div>

      <AppErrorState v-else-if="showFullError" variant="full" @retry="refresh" />

      <template v-else>
        <CartoonProfileHeader
          v-if="profile"
          :profile="profile"
          :is-mine="isMine"
          :following="isFollowing"
          :follow-loading="followLoading"
          :show-follow-button="userStore.isLoggedIn()"
          @follow-click="handleFollowClick"
          @author-click="openAuthor(profile)"
        />

        <div
          v-else-if="showInitialSkeleton"
          class="cartoon-page__header-skeleton"
          aria-label="角色信息加载中"
        >
          <div class="cartoon-page__avatar-skeleton" />
          <div class="cartoon-page__line-skeleton is-name" />
          <div class="cartoon-page__line-skeleton is-short" />
          <div class="cartoon-page__line-skeleton is-stats" />
          <div class="cartoon-page__line-skeleton is-wide" />
          <div class="cartoon-page__button-skeleton" />
        </div>

        <div class="cartoon-page__tabs" role="tablist" aria-label="角色主页分类">
          <ProfileWorkFilterDropdown
            :active="activeTab === 'works'"
            :model-value="activeWorkFilter"
            :options="CARTOON_WORK_FILTERS"
            @activate="setActiveTab('works')"
            @update:model-value="handleWorkFilterChange"
          />
          <button
            type="button"
            class="cartoon-page__tab"
            :class="{ 'is-active': activeTab === 'appearances' }"
            role="tab"
            :aria-selected="activeTab === 'appearances'"
            @click="setActiveTab('appearances')"
          >
            出镜
          </button>
        </div>

        <div class="cartoon-page__content">
          <AppErrorState v-if="error" variant="inline" @retry="refresh" />

          <div v-else-if="showContentLoginPrompt" class="cartoon-page__login">
            <LoginPrompt :message="contentLoginMessage" />
          </div>

          <div v-else class="cartoon-page__grid" :style="gridStyle">
            <template v-if="showInitialSkeleton">
              <div
                v-for="(column, columnIndex) in skeletonColumns"
                :key="`skeleton-column-${columnIndex}`"
                class="cartoon-page__column"
              >
                <div v-for="index in column" :key="index" class="cartoon-page__card-skeleton">
                  <div class="cartoon-page__cover-skeleton" />
                  <div class="cartoon-page__card-body-skeleton">
                    <div class="cartoon-page__line-skeleton is-card-title" />
                    <div class="cartoon-page__line-skeleton is-card-author" />
                    <div class="cartoon-page__line-skeleton is-card-short" />
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <div
                v-for="(column, columnIndex) in workColumns"
                :key="`column-${columnIndex}`"
                class="cartoon-page__column"
              >
                <SummaryContentCard
                  v-for="(item, index) in column"
                  :key="item.id"
                  :item="item"
                  :priority="!runtimeBudget.isConstrained && columnIndex === 0 && index < 2"
                  @click="openWork"
                  @author-click="openAuthor"
                />
              </div>
            </template>
          </div>

          <div v-if="showEmpty" class="cartoon-page__state cartoon-page__state--fill">
            <img
              class="cartoon-page__state-image"
              :src="stateImages.empty"
              alt="空状态"
              width="160"
              height="160"
              draggable="false"
            />
            <p>{{ stateMessage }}</p>
          </div>

          <div
            v-if="hasNext || loadingMore"
            ref="loadMoreTarget"
            class="cartoon-page__loading"
            aria-live="polite"
          >
            <img
              v-if="loadingMore"
              class="cartoon-page__loading-image"
              :src="loadingImage"
              alt="加载中"
              width="64"
              height="64"
              draggable="false"
            />
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<style scoped>
.cartoon-page {
  min-width: 0;
  min-height: calc(100vh - 16px);
  border-radius: 18px;
  background: var(--profile-bg);
  color: var(--profile-text);
}

.cartoon-page__inner {
  display: flex;
  min-height: inherit;
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
  padding-bottom: 8px;
}

.cartoon-page__tabs {
  position: sticky;
  top: 0;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: var(--profile-bg);
}

.cartoon-page__content {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.cartoon-page__login {
  display: flex;
  min-height: 320px;
  flex: 1 1 auto;
}

.cartoon-page__tab {
  position: relative;
  padding: 12px 12px 8px;
  color: var(--profile-muted);
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
}

.cartoon-page__tab.is-active {
  color: var(--profile-text);
}

.cartoon-page__tab.is-active::after {
  position: absolute;
  right: 12px;
  bottom: 3px;
  left: 12px;
  height: 1px;
  border-radius: 999px;
  background: var(--profile-text);
  content: '';
}

.cartoon-page__grid {
  display: grid;
  grid-template-columns: repeat(var(--cartoon-grid-columns), minmax(0, 1fr));
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
}

.cartoon-page__column {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.cartoon-page__state {
  display: flex;
  min-height: 240px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--profile-muted);
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  padding: 24px 16px;
}

.cartoon-page__state--fill {
  min-height: 0;
  flex: 1 1 auto;
}

.cartoon-page__state--stage {
  min-height: calc(100vh - 96px);
  gap: 12px;
  animation: cartoon-state-enter 0.26s ease both;
}

.cartoon-page__state p {
  margin: 0;
}

.cartoon-page__state-copy {
  display: flex;
  max-width: min(100%, 360px);
  flex-direction: column;
  gap: 6px;
}

.cartoon-page__state-title {
  margin: 0;
  color: var(--profile-text);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.cartoon-page__state-subtitle {
  margin: 0;
  color: var(--profile-muted);
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.cartoon-page__state-image {
  width: var(--app-state-illustration-size);
  height: var(--app-state-illustration-size);
  object-fit: contain;
  opacity: 0.72;
}

.cartoon-page__state--stage .cartoon-page__state-image {
  width: var(--app-state-illustration-size-large);
  height: var(--app-state-illustration-size-large);
  opacity: 1;
}

.cartoon-page__state-action {
  display: inline-flex;
  min-width: 128px;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
  padding: 8px 18px;
  border-radius: 999px;
  background: var(--profile-text);
  color: var(--profile-bg);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  outline: none;
  transition:
    opacity var(--app-interaction-duration) var(--app-interaction-ease),
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.cartoon-page__state-action:hover,
.cartoon-page__state-action:focus-visible {
  opacity: 0.86;
}

.cartoon-page__state-action:focus-visible {
  box-shadow:
    0 0 0 3px var(--profile-bg),
    0 0 0 5px var(--profile-text);
}

.cartoon-page__state-action:active {
  transform: scale(var(--app-interaction-standard-active-scale));
}

.cartoon-page__loading {
  display: flex;
  min-height: 112px;
  align-items: center;
  justify-content: center;
  padding: 24px 0;
}

.cartoon-page__loading-image {
  width: 64px;
  height: 64px;
  object-fit: cover;
  opacity: 0.72;
}

.cartoon-page__header-skeleton {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 24px 24px;
}

.cartoon-page__avatar-skeleton,
.cartoon-page__line-skeleton,
.cartoon-page__button-skeleton,
.cartoon-page__cover-skeleton {
  overflow: hidden;
}

.cartoon-page__avatar-skeleton::after,
.cartoon-page__line-skeleton::after,
.cartoon-page__button-skeleton::after,
.cartoon-page__cover-skeleton::after {
  display: block;
  width: 100%;
  height: 100%;
  animation: cartoon-shimmer 1.9s ease-in-out infinite;
  background: linear-gradient(
    110deg,
    rgb(255 255 255 / 0) 0%,
    rgb(255 255 255 / 0) 38%,
    var(--app-skeleton-shimmer) 50%,
    rgb(255 255 255 / 0) 62%,
    rgb(255 255 255 / 0) 100%
  );
  content: '';
  transform: translateX(-120%);
}

.cartoon-page__avatar-skeleton {
  width: 138px;
  height: 138px;
  border-radius: 50%;
  background: var(--app-skeleton-base);
}

.cartoon-page__line-skeleton {
  width: 130px;
  height: 16px;
  border-radius: 999px;
  background: var(--app-skeleton-line);
}

.cartoon-page__line-skeleton.is-name {
  width: 88px;
  height: 24px;
}

.cartoon-page__line-skeleton.is-short {
  width: 72px;
}

.cartoon-page__line-skeleton.is-stats {
  width: 220px;
}

.cartoon-page__line-skeleton.is-wide {
  width: min(464px, 72vw);
}

.cartoon-page__button-skeleton {
  width: 104px;
  height: 40px;
  border-radius: 999px;
  background: var(--app-skeleton-line);
}

.cartoon-page__card-skeleton {
  display: inline-flex;
  width: 100%;
  break-inside: avoid;
  flex-direction: column;
  gap: 0;
  padding: 12px 12px 16px;
}

.cartoon-page__cover-skeleton {
  aspect-ratio: 293 / 468.8;
  border-radius: 12px;
  background: var(--app-skeleton-base);
}

.cartoon-page__card-body-skeleton {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 8px 0;
}

.cartoon-page__line-skeleton.is-card-title {
  width: 78%;
  height: 18px;
}

.cartoon-page__line-skeleton.is-card-author {
  width: 42%;
}

.cartoon-page__line-skeleton.is-card-short {
  width: 36%;
  height: 12px;
}

@keyframes cartoon-shimmer {
  0% {
    transform: translateX(-120%);
  }

  52%,
  to {
    transform: translateX(120%);
  }
}

@keyframes cartoon-state-enter {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 900px) {
  .cartoon-page {
    border-radius: 14px;
  }
}

@media (max-width: 640px) {
  .cartoon-page {
    min-height: calc(100vh - 8px);
    border-radius: 0;
  }

  .cartoon-page__tabs {
    top: 0;
  }

  .cartoon-page__tab {
    padding: 10px 12px 6px;
    font-size: 15px;
    line-height: 22px;
  }

  .cartoon-page__filters {
    justify-content: flex-start;
    overflow-x: auto;
    padding: 4px 10px 8px;
  }

  .cartoon-page__filter {
    flex: 0 0 auto;
  }

  .cartoon-page__grid {
    padding: 0 6px;
  }

  .cartoon-page__card-body-skeleton {
    padding: 7px 2px 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cartoon-page__state--stage {
    animation: none;
  }

  .cartoon-page__state-action {
    transition: none;
  }

  .cartoon-page__avatar-skeleton::after,
  .cartoon-page__line-skeleton::after,
  .cartoon-page__button-skeleton::after,
  .cartoon-page__cover-skeleton::after {
    animation: none;
  }
}
</style>
